import { useNavigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import { useResourceMapped } from '../lib/api';
import { mapArt14, mapTask, mapPortfolio, type ApiArt14Row, type ApiTaskRow, type ApiPortfolio } from '../lib/adapt';
import type { Art14Row, Portfolio, TaskRow } from '../lib/contract';
import { stagesFor, remainingLabel, VULN_STAGES } from '../lib/cra';
import { Badge, Btn, Card, SectionHead } from '../components/ui/primitives';
import { Countdown, Loading } from '../components/ui/widgets';

function greeting(name: string): string {
  const h = new Date().getHours();
  const g = h < 5 ? 'night' : h < 12 ? 'morning' : h < 14 ? 'noon' : h < 19 ? 'afternoon' : 'night';
  return `Good ${g}, ${name.split(' ')[0]}. Here is what we have for today:`;
}

/** Urgency rank for the feed: overdue first, then open KEV, then everything else. */
function urgency(a: Art14Row): number {
  if (a.status === 'overdue') return 0;
  if (a.isKev) return 1;
  return 2;
}

export function Today() {
  const { session } = useSession();
  const navigate = useNavigate();
  const tasks = useResourceMapped<ApiTaskRow[], TaskRow[]>(session, '/api/tasks', (rows) => rows.map(mapTask));
  const art14 = useResourceMapped<ApiArt14Row[], Art14Row[]>(session, '/api/art14', (rows) => rows.map(mapArt14));
  const portfolio = useResourceMapped<ApiPortfolio, Portfolio>(session, '/api/portfolio', mapPortfolio);

  if (tasks.loading || art14.loading || portfolio.loading) return <Loading />;

  const incidents = art14.data ?? [];
  const openIncidents = incidents.filter((a) => a.status !== 'notified');
  const openKev = openIncidents.filter((a) => a.isKev);
  const overdue = openIncidents.filter((a) => a.status === 'overdue');
  const sortedOpen = [...openIncidents].sort((a, b) => urgency(a) - urgency(b) || Date.parse(a.detectedAt) - Date.parse(b.detectedAt));

  // Findings + SBOM coverage from the real portfolio read model — no fixtures.
  const products = portfolio.data?.products ?? [];
  const applicableFindings = products.reduce((n, p) => n + p.openFindings, 0);
  const productsWithFindings = products.filter((p) => p.openFindings > 0).length;
  const releaseVersions = products.flatMap((p) => p.versions).filter((v) => (v.releaseStatus ?? (v.release ? 'release' : 'internal')) === 'release');
  const covered = releaseVersions.filter((v) => v.sbomStatus === 'complete').length;
  const needSbom = releaseVersions.filter((v) => v.sbomStatus === 'none' || v.sbomStatus === 'failed').length;

  // Non-Art.14 work (warnings/manual). Art.14 incidents are rendered directly
  // below, so they aren't duplicated from the task feed here.
  const otherTasks = (tasks.data ?? []).filter((t) => t.source !== 'art14_event');

  // The hero highlights the most urgent open KEV; its "View findings" resolves to
  // the real product-version analysis via the portfolio (product + version match).
  const top = sortedOpen.find((a) => a.isKev) ?? sortedOpen[0];
  const heroDeadline = top ? VULN_STAGES[0].deadline({ t0: Date.parse(top.detectedAt), fixAvailableAt: null, notified72At: null }) : Date.now();
  const analysisPathFor = (a: Art14Row): string | null => {
    const p = products.find((pr) => pr.name === a.productName);
    const v = p?.versions.find((ver) => ver.label === a.versionLabel);
    return p && v ? `/app/products/${p.id}/analysis/${v.id}` : null;
  };
  const topAnalysis = top ? analysisPathFor(top) : null;

  return (
    <>
      <div>
        <h1 style={{ fontSize: 'calc(27px*var(--fs))' }}>{greeting(session.userName)}</h1>
      </div>

      {top && (
        <Card className="hero" style={{ padding: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center' }} className="hero-grid">
            <div>
              <div className="hero-eyebrow" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {top.isKev && <Badge kind="kev">[K] KEV</Badge>}
                <span className="mono" style={{ fontSize: 'calc(13px*var(--fs))' }}>{top.cveId}</span>
                <Badge kind="crit">Art.14 · early warning</Badge>
              </div>
              <h2 style={{ marginTop: 8 }}>{top.component ? `${top.component} in ` : ''}{top.productName} {top.versionLabel}</h2>
              <div className="detail" style={{ color: 'var(--muted)', marginTop: 8 }}>
                Actively exploited — a 24-hour ENISA early-warning obligation is open.
              </div>
              <div className="hero-actions" style={{ marginTop: 16 }}>
                <Btn variant="primary" onClick={() => navigate(`/app/incidents/${top.id}`)}>Open incident →</Btn>
                {topAnalysis && <Btn onClick={() => navigate(topAnalysis)}>View findings</Btn>}
              </div>
            </div>
            <Countdown deadlineMs={heroDeadline} cap="until ENISA early-warning deadline" t0Iso={top.detectedAt} />
          </div>
        </Card>
      )}

      <div className="stats">
        <div className="card stat" onClick={() => navigate('/app/incidents')}>
          <div className="k">Open Art.14 obligations</div>
          <div className={`v ${openIncidents.length ? 'crit' : ''}`} data-testid="stat-open-art14">{openIncidents.length}</div>
          <div className="s">{overdue.length ? <Badge kind="high" style={{ fontSize: 'calc(10px*var(--fs))' }}>{overdue.length} overdue</Badge> : 'none overdue · SLA intact'}</div>
        </div>
        <div className="card stat" onClick={() => navigate('/app/incidents')}>
          <div className="k">Actively exploited (KEV)</div>
          <div className={`v ${openKev.length ? 'crit' : ''}`} data-testid="stat-open-kev">{openKev.length}</div>
          <div className="s">{openKev.length ? <Badge kind="kev" style={{ fontSize: 'calc(10px*var(--fs))' }}>24h clock running</Badge> : 'no open KEV incidents'}</div>
        </div>
        <div className="card stat" onClick={() => navigate('/app/products')}>
          <div className="k">Applicable findings</div>
          <div className={`v ${applicableFindings ? 'warn' : ''}`} data-testid="stat-findings">{applicableFindings}</div>
          <div className="s">across {productsWithFindings} product{productsWithFindings === 1 ? '' : 's'}</div>
        </div>
        <div className="card stat" onClick={() => navigate('/app/products')}>
          <div className="k">SBOM coverage</div>
          <div className="v" data-testid="stat-coverage">{covered}<span style={{ color: 'var(--muted)', fontSize: 'calc(20px*var(--fs))' }}> / {releaseVersions.length}</span></div>
          <div className="s">{needSbom ? <><Badge kind="indet" style={{ fontSize: 'calc(10px*var(--fs))' }}>[?]</Badge> {needSbom} release version{needSbom === 1 ? '' : 's'} need an SBOM</> : 'every release version has an SBOM'}</div>
        </div>
      </div>

      <Card className="panel">
        <div className="panel-head"><SectionHead title="What needs you today" /><a onClick={() => navigate('/app/incidents')} style={{ cursor: 'pointer', color: 'var(--teal)' }}>All incidents →</a></div>
        <div style={{ padding: '6px 0' }} data-testid="today-feed">
          {sortedOpen.length === 0 && otherTasks.length === 0 && (
            <div className="task" style={{ padding: '14px 18px', color: 'var(--muted)' }} data-testid="today-empty">
              Nothing open right now — upload an SBOM to start monitoring, or you're all caught up.
            </div>
          )}
          {/* Open Art.14 incidents first — every KEV is here and opens its report. */}
          {sortedOpen.map((a) => {
            const stages = stagesFor(a.kind);
            const stage = stages.find((s) => s.key === a.milestone) ?? stages[0];
            const dl = stage.deadline({ t0: Date.parse(a.detectedAt), fixAvailableAt: a.fixAvailableAt ? Date.parse(a.fixAvailableAt) : null, notified72At: a.notifiedAt ? Date.parse(a.notifiedAt) : null });
            const rem = remainingLabel(dl, Date.now());
            return (
              <div key={a.id} className="task" data-testid="today-incident" style={{ display: 'flex', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--border)', cursor: 'pointer', alignItems: 'center' }} onClick={() => navigate(`/app/incidents/${a.id}`)}>
                <div style={{ flex: 1 }}>
                  <div className="t" style={{ fontWeight: 500 }}>{a.productName} {a.versionLabel}{a.component ? ` · ${a.component}` : ''}</div>
                  <div className="m" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center', color: 'var(--muted)', marginTop: 2, fontSize: 'calc(12px*var(--fs))' }}>
                    {a.isKev ? <Badge kind="kev">KEV · Art.14</Badge> : <Badge kind="crit">Art.14</Badge>}
                    <span className="mono">{a.cveId}</span>
                    <span>· {stage.label}</span>
                  </div>
                </div>
                <div className="due" style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'calc(12px*var(--fs))' }}>
                  <b style={{ display: 'block', color: rem.overdue ? 'var(--critical)' : 'var(--medium)' }}>{a.status === 'notified' ? 'sent ✓' : rem.waiting ? '—' : rem.text}</b>
                  <span style={{ color: 'var(--muted)' }}>Open incident →</span>
                </div>
              </div>
            );
          })}
          {/* Then warnings / manual tasks. */}
          {otherTasks.map((t) => (
            <div key={t.id} className="task" style={{ display: 'flex', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--border)', cursor: 'pointer', alignItems: 'center' }} onClick={() => navigate('/app' + t.nav)}>
              <div style={{ flex: 1 }}>
                <div className="t" style={{ fontWeight: 500 }}>{t.title}</div>
                <div className="m" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center', color: 'var(--muted)', marginTop: 2, fontSize: 'calc(12px*var(--fs))' }}>
                  {t.cveId && <span className="mono">{t.cveId}</span>}
                  {t.productName && <span>· {t.productName}</span>}
                </div>
              </div>
              <div className="due" style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'calc(12px*var(--fs))', color: 'var(--muted)' }}>
                {t.assigneeUserRef ? 'assigned' : 'unassigned'}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
